import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type TtbPaymentModuleSpecsFragment } from '../../../../api/fragments/TtbPaymentModuleSpecs';
import {
    type DeleteTtbPaymentSettingsMutation,
    type DeleteTtbPaymentSettingsMutationVariables,
    DeleteTtbPaymentSettingsDocument,
} from '../../../../api/mutations/deleteTtbPaymentSettings';

export type TtbPaymentSettingActionsProps = {
    module: TtbPaymentModuleSpecsFragment;
    paymentSetting: TtbPaymentModuleSpecsFragment['paymentSettings'][number];
};

const TtbPaymentSettingActions = ({ module, paymentSetting }: TtbPaymentSettingActionsProps) => {
    const moduleId = module.id;
    const paymentSettingId = paymentSetting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['ttbPaymentModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('ttbPaymentModuleDetails:paymentSettings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('ttbPaymentModuleDetails:paymentSettings.deleteModal.content'),
            okText: t('ttbPaymentModuleDetails:paymentSettings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('ttbPaymentModuleDetails:paymentSettings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('ttbPaymentModuleDetails:paymentSettings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteTtbPaymentSettingsMutation, DeleteTtbPaymentSettingsMutationVariables>({
                        mutation: DeleteTtbPaymentSettingsDocument,
                        variables: { moduleId, id: paymentSettingId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('ttbPaymentModuleDetails:paymentSettings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, paymentSettingId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/ttbPayment/${paymentSettingId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('ttbPaymentModuleDetails:paymentSettingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('ttbPaymentModuleDetails:paymentSettingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default TtbPaymentSettingActions;
